import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { Package, Image, Mail, MessageSquare, Settings, LogOut, Plus, Trash2, Edit, X, Save, ShoppingBag, RefreshCw, ExternalLink, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SMILEY_LOGO = "https://customer-assets.emergentagent.com/job_crookid-shop/artifacts/p49bhm0j_HaveTheDayYouDeserveVECTOR.JPEG";

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [products, setProducts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [orders, setOrders] = useState([]);
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  const authHeader = `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`;

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Authorization': authHeader }
      });
      if (res.ok) {
        setIsAuthenticated(true);
        toast.success('Logged in successfully');
      } else {
        toast.error('Invalid credentials');
      }
    } catch (error) {
      toast.error('Login failed');
    }
  };

  const fetchShopifyStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/shopify/status`);
      setShopifyStatus(await res.json());
    } catch (error) {
      // Error fetching Shopify status
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'products') {
        const res = await fetch(`${API}/products`);
        setProducts(await res.json());
      } else if (activeTab === 'shopify') {
        const res = await fetch(`${API}/shopify/products`);
        setProducts(await res.json());
      } else if (activeTab === 'gallery') {
        const res = await fetch(`${API}/gallery`);
        setGallery(await res.json());
      } else if (activeTab === 'contacts') {
        const res = await fetch(`${API}/admin/contacts`, { headers: { 'Authorization': authHeader } });
        setContacts(await res.json());
      } else if (activeTab === 'newsletter') {
        const res = await fetch(`${API}/admin/newsletter`, { headers: { 'Authorization': authHeader } });
        setNewsletter(await res.json());
      } else if (activeTab === 'orders') {
        const res = await fetch(`${API}/admin/orders`, { headers: { 'Authorization': authHeader } });
        setOrders(await res.json());
      }
    } catch (error) {
      // Error fetching data
    } finally {
      setLoading(false);
    }
  }, [activeTab, authHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      fetchShopifyStatus();
    }
  }, [isAuthenticated, activeTab, fetchData, fetchShopifyStatus]);

  const handleShopifySync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/shopify/sync-products`, {
        method: 'POST',
        headers: { 'Authorization': authHeader }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchData();
      } else if (res.status === 400 && data.detail?.includes('429')) {
        toast.error('Shopify is rate limiting requests. Please wait a minute and try again.');
      } else {
        toast.error(data.detail || 'Sync failed');
      }
    } catch (error) {
      toast.error('Error syncing products. Please try again in a moment.');
    } finally {
      setSyncing(false);
    }
  };


  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await fetch(`${API}/admin/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': authHeader }
      });
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Error deleting product');
    }
  };

  const handleDeleteGalleryImage = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await fetch(`${API}/admin/gallery/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': authHeader }
      });
      toast.success('Image deleted');
      fetchData();
    } catch (error) {
      toast.error('Error deleting image');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="admin-login">
        <Toaster position="top-right" richColors />
        <div className="bg-cs-card border border-cs-border p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <img src={SMILEY_LOGO} alt="Logo" className="w-16 h-16 mx-auto mb-4" />
            <h1 className="font-anton text-3xl uppercase">Admin Login</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={e => setCredentials({ ...credentials, username: e.target.value })}
                className="brutalist-input w-full"
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={e => setCredentials({ ...credentials, password: e.target.value })}
                className="brutalist-input w-full"
                data-testid="admin-password"
              />
            </div>
            <button type="submit" className="btn-primary w-full" data-testid="admin-login-btn">
              Login
            </button>
          </form>
          <p className="font-mono text-sm text-cs-muted text-center mt-4">
            Default: admin / CrookidSmiles2024!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cs-bg" data-testid="admin-dashboard">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-cs-dark border-b border-cs-border text-cs-text py-4 px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={SMILEY_LOGO} alt="Logo" className="w-10 h-10" />
          <span className="font-anton text-xl uppercase text-cs-text">Admin Dashboard</span>
        </div>
        <button 
          onClick={() => setIsAuthenticated(false)}
          className="flex items-center gap-2 font-mono text-sm text-cs-muted hover:text-cs-ochre transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" /> Logout
        </button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-cs-bg2 border-r border-cs-border min-h-[calc(100vh-64px)] p-4">
          {/* Shopify Connection Status */}
          <div className="mb-6 p-4 border border-cs-olive bg-cs-olive/10 rounded">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingBag className="w-5 h-5 text-cs-olive" />
              <span className="font-mono text-sm text-cs-text font-semibold">Shopify</span>
            </div>
            {shopifyStatus?.connected ? (
              <div>
                <p className="font-mono text-xs text-cs-olive mb-1">Connected to {shopifyStatus.shop}</p>
                {shopifyStatus.synced_products > 0 && (
                  <p className="font-mono text-xs text-cs-muted mb-2">{shopifyStatus.synced_products} products synced</p>
                )}
                <button
                  onClick={handleShopifySync}
                  disabled={syncing}
                  className="w-full flex items-center justify-center gap-2 bg-cs-olive text-cs-text py-2 px-3 text-sm font-mono hover:bg-cs-olive2 transition-colors"
                  data-testid="sync-shopify"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync Products'}
                </button>
              </div>
            ) : (
              <p className="font-mono text-xs text-cs-muted">
                No Shopify store configured
              </p>
            )}
          </div>

          <nav className="space-y-2">
            {[
              { id: 'products', icon: Package, label: 'Products' },
              { id: 'shopify', icon: ShoppingBag, label: 'Shopify Products' },
              { id: 'orders', icon: Package, label: 'Orders' },
              { id: 'contacts', icon: MessageSquare, label: 'Contact Messages' },
              { id: 'newsletter', icon: Mail, label: 'Newsletter' },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 p-3 font-mono text-sm uppercase tracking-wider transition-colors ${
                  activeTab === item.id
                    ? 'bg-cs-olive text-cs-text border border-cs-olive'
                    : 'text-cs-muted hover:bg-cs-card hover:text-cs-text border border-transparent'
                }`}
                data-testid={`tab-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-2 border-cs-ochre border-t-transparent animate-spin" />
            </div>
          ) : (
            <>
              {/* Products Tab */}
              {activeTab === 'products' && (
                <div data-testid="products-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-anton text-2xl uppercase text-cs-text">Products ({products.length})</h2>
                    <button 
                      onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
                      className="btn-primary flex items-center gap-2"
                      data-testid="add-product-btn"
                    >
                      <Plus className="w-5 h-5" /> Add Product
                    </button>
                  </div>
                  <div className="grid gap-4">
                    {products.map(product => (
                      <div key={product.id} className="bg-cs-card border border-cs-border p-4 flex items-center gap-4">
                        <img src={product.images[0]} alt={product.name} className="w-20 h-20 object-cover border border-cs-border" />
                        <div className="flex-1">
                          <h3 className="font-anton text-lg uppercase text-cs-text">{product.name}</h3>
                          <p className="font-mono text-sm text-cs-muted">{product.category} | ${product.price}</p>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                            className="p-2 border border-cs-border text-cs-muted hover:bg-cs-olive hover:text-cs-text hover:border-cs-olive transition-colors"
                            data-testid={`edit-${product.id}`}
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={() => handleDeleteProduct(product.id)}
                            className="p-2 border border-cs-alert/50 text-cs-alert/70 hover:bg-cs-alert hover:text-cs-text hover:border-cs-alert transition-colors"
                            data-testid={`delete-${product.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shopify Products Tab */}
              {activeTab === 'shopify' && (
                <div data-testid="shopify-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-anton text-2xl uppercase text-cs-text">Shopify Products ({products.length})</h2>
                    <button 
                      onClick={handleShopifySync}
                      disabled={syncing}
                      className="btn-primary flex items-center gap-2"
                      data-testid="sync-shopify-btn"
                    >
                      <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                      {syncing ? 'Syncing...' : 'Sync Now'}
                    </button>
                  </div>
                  
                  {products.length === 0 ? (
                    <div className="bg-cs-card border border-cs-border p-8 text-center">
                      <Package className="w-16 h-16 mx-auto mb-4 text-cs-muted" />
                      <h3 className="font-anton text-xl uppercase mb-2 text-cs-text">No Products Synced Yet</h3>
                      <p className="font-mono text-sm text-cs-muted mb-4">
                        Click "Sync Now" to fetch products from your Shopify store ({shopifyStatus?.shop}).
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {products.map(product => (
                        <div key={product.id} className="bg-cs-card border border-cs-border p-4 flex items-center gap-4">
                          <img 
                            src={product.images?.[0] || 'https://via.placeholder.com/80'} 
                            alt={product.title} 
                            className="w-20 h-20 object-cover border border-cs-border" 
                          />
                          <div className="flex-1">
                            <h3 className="font-anton text-lg uppercase text-cs-text">{product.title}</h3>
                            <p className="font-mono text-sm text-cs-muted">
                              ${product.price} | {product.variants?.length || 0} variants
                            </p>
                            <p className="font-mono text-xs text-cs-olive mt-1">
                              Synced from Shopify
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <a 
                              href={product.shopify_url || `https://${shopifyStatus?.shop}/products/${product.handle}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 border border-cs-border text-cs-muted hover:bg-cs-olive hover:border-cs-olive hover:text-cs-text transition-colors"
                            >
                              <ExternalLink className="w-5 h-5" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div data-testid="orders-panel">
                  <h2 className="font-anton text-2xl uppercase mb-6 text-cs-text">Orders ({orders.length})</h2>
                  <div className="space-y-4">
                    {orders.map(order => (
                      <div key={order.id} className="bg-cs-card border border-cs-border p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-sm font-semibold text-cs-text">#{order.id.slice(0, 8).toUpperCase()}</span>
                          <span className={`font-outfit text-sm px-2 py-1 ${
                            order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.payment_status}
                          </span>
                        </div>
                        <p className="font-mono text-sm text-cs-muted">{order.email}</p>
                        <p className="font-mono font-bold mt-2 text-cs-ochre">${order.total.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div data-testid="contacts-panel">
                  <h2 className="font-anton text-2xl uppercase mb-6 text-cs-text">Contact Messages ({contacts.length})</h2>
                  <div className="space-y-4">
                    {contacts.map(contact => (
                      <div key={contact.id} className="bg-cs-card border border-cs-border p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-sm font-semibold text-cs-text">{contact.name}</span>
                          <span className="font-mono text-xs bg-cs-olive/20 text-cs-text px-2 py-1">{contact.subject}</span>
                        </div>
                        <p className="font-mono text-sm text-cs-muted">{contact.email}</p>
                        <p className="font-mono text-sm text-cs-text mt-2">{contact.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter Tab */}
              {activeTab === 'newsletter' && (
                <div data-testid="newsletter-panel">
                  <h2 className="font-anton text-2xl uppercase mb-6">Newsletter Subscribers ({newsletter.length})</h2>
                  <div className="bg-cs-card border border-cs-border">
                    <table className="w-full">
                      <thead className="bg-cs-olive text-cs-text">
                        <tr>
                          <th className="p-3 text-left font-outfit font-semibold">Email</th>
                          <th className="p-3 text-left font-outfit font-semibold">Subscribed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newsletter.map((sub, i) => (
                          <tr key={sub.id} className={i % 2 === 0 ? 'bg-cs-card' : 'bg-cs-bg'}>
                            <td className="p-3 font-mono text-sm text-cs-text">{sub.email}</td>
                            <td className="p-3 font-mono text-sm text-cs-muted">
                              {new Date(sub.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <ProductModal 
          product={editingProduct}
          authHeader={authHeader}
          onClose={() => setShowProductModal(false)}
          onSave={() => { setShowProductModal(false); fetchData(); }}
        />
      )}

      {/* Gallery Modal */}
      {showGalleryModal && (
        <GalleryModal 
          authHeader={authHeader}
          onClose={() => setShowGalleryModal(false)}
          onSave={() => { setShowGalleryModal(false); fetchData(); }}
        />
      )}
    </div>
  );
};

const ProductModal = ({ product, authHeader, onClose, onSave }) => {
  const [form, setForm] = useState(product || {
    name: '',
    description: '',
    price: '',
    category: 'hoodies',
    images: [''],
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    colors: ['Black'],
    material: '',
    care_instructions: '',
    featured: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = product 
        ? `${API}/admin/products/${product.id}`
        : `${API}/admin/products`;
      const method = product ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader 
        },
        body: JSON.stringify({
          ...form,
          price: parseFloat(form.price),
          images: form.images.filter(i => i.trim())
        })
      });
      
      if (res.ok) {
        toast.success(product ? 'Product updated' : 'Product created');
        onSave();
      } else {
        toast.error('Error saving product');
      }
    } catch (error) {
      toast.error('Error saving product');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="product-modal">
      <div className="bg-cs-bg2 border border-cs-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-cs-border">
          <h2 className="font-anton text-xl uppercase text-cs-text">{product ? 'Edit Product' : 'Add Product'}</h2>
          <button onClick={onClose} className="p-2 text-cs-muted hover:text-cs-text transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
              className="brutalist-input w-full"
            />
          </div>
          <div>
            <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              required
              rows={3}
              className="brutalist-input w-full"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })}
                required
                className="brutalist-input w-full"
              />
            </div>
            <div>
              <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Category</label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="brutalist-input w-full"
              >
                <option value="hoodies">Hoodies</option>
                <option value="tshirts">T-Shirts</option>
                <option value="crewnecks">Crewnecks</option>
                <option value="hats">Hats</option>
                <option value="jackets">Jackets</option>
                <option value="accessories">Accessories</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Image URL</label>
            <input
              type="url"
              value={form.images[0]}
              onChange={e => setForm({ ...form, images: [e.target.value] })}
              required
              className="brutalist-input w-full"
              placeholder="https://..."
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={e => setForm({ ...form, featured: e.target.checked })}
              className="w-5 h-5"
            />
            <label className="font-mono text-sm text-cs-text">Featured Product</label>
          </div>
          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2">
              <Save className="w-5 h-5" /> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const GalleryModal = ({ authHeader, onClose, onSave }) => {
  const [form, setForm] = useState({
    url: '',
    alt: '',
    category: 'adults'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/admin/gallery`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader 
        },
        body: JSON.stringify(form)
      });
      
      if (res.ok) {
        toast.success('Image added');
        onSave();
      } else {
        toast.error('Error adding image');
      }
    } catch (error) {
      toast.error('Error adding image');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="gallery-modal">
      <div className="bg-cs-bg2 border border-cs-border max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-cs-border">
          <h2 className="font-anton text-xl uppercase text-cs-text">Add Gallery Image</h2>
          <button onClick={onClose} className="p-2 text-cs-muted hover:text-cs-text transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Image URL</label>
            <input
              type="url"
              value={form.url}
              onChange={e => setForm({ ...form, url: e.target.value })}
              required
              className="brutalist-input w-full"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Alt Text</label>
            <input
              type="text"
              value={form.alt}
              onChange={e => setForm({ ...form, alt: e.target.value })}
              required
              className="brutalist-input w-full"
            />
          </div>
          <div>
            <label className="font-mono text-sm text-cs-text uppercase tracking-wider block mb-2">Category</label>
            <select
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              className="brutalist-input w-full"
            >
              <option value="kids">Kids</option>
              <option value="adults">Adults</option>
              <option value="on_the_field">On the Field</option>
            </select>
          </div>
          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1">
              Add Image
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Admin;
